import { useState } from 'react';
import { ArrowRightIcon, BookmarkIcon, BotIcon, CheckIcon, DatabaseIcon, FileKeyIcon, FileTextIcon, HashIcon, Link2Icon, MessageSquareIcon, PlusIcon, SparklesIcon, UserIcon, WandSparklesIcon } from 'lucide-react';
import { activityFor, attentionCountFor, chatsFor, linkedFor, statePlayFor, type ActivityEntry, type ActivityTag, type LinkedCategory } from '../../data/trackDetail';
import { avatarHue, type Hue } from '../alt/hues';
import { EmptyState, Glyph, MetaStat, Panel, Row, RowText, SearchField, SelectPill } from '../dashboard/ui';

/**
 * The three non-chat track views. Every neutral here is a semantic --app-*
 * token (see components/dashboard/ui.tsx): near-white canvas, white
 * surfaces, neutral hairlines. Colour appears only where it carries meaning
 * — the group's own hue, and the event-type legend dots.
 */

export type TrackDetailViewProps = {
  trackName: string;
  group: string;
  groupHue: Hue;
  exportMode?: boolean;
};

// Legend-style dot + label rather than a filled badge: a row of solid pills
// read as busy the moment more than one tag applied. These are the shared
// status tokens, so "issue" is the same red everywhere in the product.
const TAG_DOT: Record<ActivityTag, string> = {
  Discussion: 'var(--app-info)',
  Decision: 'var(--app-success)',
  Issue: 'var(--app-danger)',
  Progress: '#6D4CBF',
  'Customer signal': 'var(--app-warning)',
  Metric: '#4159A6'
};
const CATEGORY_ICON: Record<string, typeof BookmarkIcon> = {
  bookmarks: BookmarkIcon,
  prompts: WandSparklesIcon,
  skills: SparklesIcon,
  credentials: FileKeyIcon,
  databases: DatabaseIcon,
  slack: HashIcon,
  chats: MessageSquareIcon,
  context: FileTextIcon
};
export function TrackOverviewView({
  trackName,
  group,
  groupHue,
  exportMode = false
}: TrackDetailViewProps) {
  const activity = activityFor(trackName);
  const recent = activity.flatMap(day => day.entries).slice(0, 5);
  const chats = chatsFor(trackName);
  const linked = linkedFor(trackName);
  const allLinked = [...linked.left, ...linked.right];
  const context = allLinked.find(category => category.id === 'context')?.items ?? [];
  const primaryChannel = allLinked.find(category => category.id === 'slack')?.items[0];
  if (exportMode) {
    return <TrackOverviewExport trackName={trackName} group={group} groupHue={groupHue} recent={recent} data-id="element-3950" data-mp="3et7vbwykpzou" />;
  }
  return <div className="mx-auto grid w-full max-w-[1040px] grid-cols-1 gap-x-8 gap-y-8 xl:grid-cols-12" data-id="element-3951" data-mp="3jnc32ifnybqz">
      <section className="xl:col-span-7" data-id="element-3952" data-mp="3omewt6q70vjn">
        {/* ONE headline, set directly on the canvas rather than boxed — the
         content is the subject, not a card containing it. */}
        <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-3953" data-mp="1g5y7lyqss8n">
          <h2 className="text-[17px] font-semibold text-app" data-id="element-3954" data-mp="6do6rb8ul5g4">State of play</h2>
          <span className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[12px] font-semibold" style={{
          backgroundColor: groupHue.tint,
          color: groupHue.ink
        }} data-id="element-3955" data-mp="237iw3r2alzv7">
            <span className="size-1.5 rounded-full" style={{
            backgroundColor: groupHue.solid
          }} data-id="element-3956" data-mp="20mkxcarwofpf" />
            {group}
          </span>
        </div>
        <p className="mt-2.5 max-w-[66ch] text-[15px] leading-relaxed text-app-muted" data-id="element-3957" data-mp="1vv2wrntsoh4z">{statePlayFor(trackName)}</p>
        <dl className="mt-5 grid grid-cols-3 gap-4 border-t border-app-divider pt-4" data-id="element-3958" data-mp="1qx9pssko3j3n">
          <MetaStat label="Active chats" value={chats.length} data-id="element-3959" data-mp="1lyzumhyy0ii1" />
          <MetaStat label="Recent events" value={recent.length} data-id="element-3960" data-mp="2jl5pm65j9r4n" />
          <MetaStat label="Needs review" value={attentionCountFor(trackName)} data-id="element-3961" data-mp="2jjcnn45v7v2z" />
        </dl>

        <h3 className="mb-2.5 mt-7 text-[13px] font-semibold text-app-muted" data-id="element-3962" data-mp="2ek9jdlxplsw2">Observations</h3>
        <Panel data-id="element-3963" data-mp="29lesdwpl8y0v">
          {recent.map((entry, index) => <Row key={`${entry.time}-${index}`} data-id="element-3964" data-mp="24oob5621rwdv" data-mpi={index}>
              <Glyph icon={entry.author === 'bot' ? BotIcon : UserIcon} data-id="element-3965" data-mp="24nwsswxgnv5j" />
              <RowText title={entry.text} meta={entry.time} data-id="element-3966" data-mp="y95n8fxe7m56" />
              <TagLegend tag={entry.tags[0]} data-id="element-3967" data-mp="t99gi8xb9glx" />
            </Row>)}
        </Panel>
      </section>

      <section className="xl:col-span-5" data-id="element-3968" data-mp="2i36xo0jiisp2">
        <h3 className="mb-2.5 text-[13px] font-semibold text-app-muted" data-id="element-3969" data-mp="2i2f4sxhav92h">Chats</h3>
        <Panel data-id="element-3970" data-mp="2mu5hvb7mgaxl">
          {chats.map(chat => <Row key={chat.ref} data-id="element-3971" data-mp="2mv87zgx4e9cg">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white" style={{
            backgroundColor: avatarHue(chat.initials)
          }} data-id="element-3972" data-mp="28512s2w4imtp">{chat.initials}</span>
              <RowText title={chat.name} meta={chat.when} data-id="element-3973" data-mp="30ty5gxnuqb1c" />
              <span className="shrink-0 font-mono text-[11px] text-app-subtle" data-id="element-3974" data-mp="30ugaaiokxxkv">{chat.ref}</span>
            </Row>)}
        </Panel>

        <h3 className="mb-2.5 mt-7 text-[13px] font-semibold text-app-muted" data-id="element-3975" data-mp="2qviqtu60gu8q">Linked</h3>
        <Panel data-id="element-3976" data-mp="2qxv4vw8nekgi">
          {context.map(item => <Row key={item.title} data-id="element-3977" data-mp="2vtsm04rqjuuc">
              <Glyph icon={FileTextIcon} data-id="element-3978" data-mp="2vt0pmrqbdt30" />
              <RowText title={item.title} meta={item.meta} data-id="element-3979" data-mp="1fglmld199ygs" />
            </Row>)}
          {primaryChannel && <Row data-id="element-3980" data-mp="1kfeyj10w9heu">
              <Glyph icon={HashIcon} data-id="element-3981" data-mp="1kfybe3botfm0" />
              <RowText title={primaryChannel.title} meta={primaryChannel.meta} data-id="element-3982" data-mp="1kgztgrr4f2d8" />
            </Row>}
        </Panel>
      </section>
    </div>;
}
function TrackOverviewExport({
  trackName,
  group,
  groupHue,
  recent
}: {
  trackName: string;
  group: string;
  groupHue: Hue;
  recent: ActivityEntry[];
}) {
  const proof = recent.slice(0, 3);
  return <div className="mx-auto flex w-full max-w-[1080px] flex-col gap-4" data-id="element-3983" data-mp="7h66zh5tbaf6">
      <div className="flex items-end justify-between gap-6" data-id="element-3984" data-mp="7f232z7wybew">
        <div data-id="element-3985" data-mp="3fxhspi4saaga">
          <h2 className="text-[19px] font-semibold tracking-[-0.015em] text-app" data-id="element-3986" data-mp="3fwy81tnr17yk">Pipeline control center</h2>
          <p className="mt-1 text-[12.5px] text-app-muted" data-id="element-3987" data-mp="3avim7zdqyxf4">Live qualification, routing, and pipeline risk in one Track.</p>
        </div>
        <span className="flex shrink-0 items-center gap-2 text-[11.5px] font-medium text-app-muted" data-id="element-3988" data-mp="3jyjrjxusgkhq">
          <span className="size-1.5 rounded-full" style={{
          backgroundColor: groupHue.solid
        }} data-id="element-3989" data-mp="2w87bu0taxjgl" />
          {group} · Updated now
        </span>
      </div>

      <div className="grid grid-cols-12 gap-4" data-id="element-3990" data-mp="2s6vjvomygm58">
        <section className="col-span-8 rounded-2xl bg-app-surface p-5 shadow-[0_1px_3px_rgba(20,20,24,0.08)]" data-id="element-3991" data-mp="v7158bbvzo0d">
          <div className="flex items-start justify-between gap-4" data-id="element-3992" data-mp="104sx55cj7a4k">
            <div data-id="element-3993" data-mp="14watfg3m0mj7">
              <p className="text-[13px] font-semibold text-app" data-id="element-3994" data-mp="14vrf09d64g9x">Lead routing today</p>
              <p className="mt-1 text-[11.5px] text-app-muted" data-id="element-3995" data-mp="19v48ve2ginm0">Giga is enriching and assigning every inbound request.</p>
            </div>
            <span className="rounded-md bg-app-subtle px-2 py-1 text-[11px] font-medium text-[var(--app-success)]" data-id="element-3996" data-mp="c8x5g7hw31b8">Running</span>
          </div>

          <div className="mt-5 flex items-stretch gap-2" aria-label="Lead qualification flow" data-id="element-3997" data-mp="h8jh4ci2k9hx">
            {[['18', 'Captured', 'Web forms'], ['18', 'Enriched', 'Company + intent'], ['14', 'Routed', 'Correct owner']].map(([value, label, detail], index) => <div key={label} className="contents" data-id="element-3998" data-mp="1xjwzqw48259r" data-mpi={index}>
                {index > 0 && <span className="flex items-center" data-id="element-3999" data-mp="22jt44wgtzypu"><ArrowRightIcon size={15} className="shrink-0 text-app-subtle" data-id="element-4000" data-mp="22iqcunflju9e" /></span>}
                <div className={`min-w-0 flex-1 rounded-xl px-4 py-4 ${index === 2 ? 'bg-app-text text-white' : 'bg-app-canvas'}`} data-id="element-4001" data-mp="3n1294bq0km5l">
                  <p className={`text-[30px] font-semibold leading-none tabular-nums ${index === 2 ? 'text-white' : 'text-app'}`} data-id="element-4002" data-mp="3i3sm12q3kmkx">{value}</p>
                  <p className={`mt-2 text-[12px] font-semibold ${index === 2 ? 'text-white' : 'text-app'}`} data-id="element-4003" data-mp="38dh4uehekgw3">{label}</p>
                  <p className={`mt-0.5 truncate text-[10.5px] ${index === 2 ? 'text-white/65' : 'text-app-subtle'}`} data-id="element-4004" data-mp="33ecx7sa4mr7v">{detail}</p>
                </div>
              </div>)}
          </div>

          <div className="mt-4 flex items-center justify-between border-t border-app-divider pt-3 text-[11px] text-app-subtle" data-id="element-4005" data-mp="2ucwp5r6sb16t">
            <span data-id="element-4006" data-mp="2pfn0idr7ytk1">78% routed without intervention</span>
            <span data-id="element-4007" data-mp="sltc6v9f9aj3">Close · Gmail · Clearbit</span>
          </div>
        </section>

        <aside className="col-span-4 rounded-2xl bg-app-surface p-5 shadow-[0_1px_3px_rgba(20,20,24,0.08)]" data-id="element-4008" data-mp="sjp8oeffqdjc">
          <div className="flex items-center justify-between gap-3" data-id="element-4009" data-mp="3sn0gq5m7fvjt">
            <p className="text-[13px] font-semibold text-app" data-id="element-4010" data-mp="3snscphjprvam">Needs attention</p>
            <span className="size-2 rounded-full bg-[var(--app-danger)]" data-id="element-4011" data-mp="1feac583pez9" />
          </div>
          <div className="mt-5" data-id="element-4012" data-mp="3iw5rqthkqhuo">
            <p className="text-[38px] font-semibold leading-none tracking-[-0.03em] text-app" data-id="element-4013" data-mp="3iv4k6yzrodhp">4</p>
            <p className="mt-2 text-[12px] leading-relaxed text-app-muted" data-id="element-4014" data-mp="3nu7djm6dwv9u">Leads are missing a company domain before CRM sync.</p>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3 border-t border-app-divider pt-4" data-id="element-4015" data-mp="3ew57utbpheg8">
            <div data-id="element-4016" data-mp="2zyuwdjz2c8ki">
              <p className="text-[19px] font-semibold text-app" data-id="element-4017" data-mp="2zzefh40g9304">$184k</p>
              <p className="mt-0.5 text-[10.5px] leading-snug text-app-muted" data-id="element-4018" data-mp="34x64nq9pmb0m">Stalled pipeline</p>
            </div>
            <div className="border-l border-app-divider pl-3" data-id="element-4019" data-mp="y6q8qkoljxfl">
              <p className="text-[19px] font-semibold text-app" data-id="element-4020" data-mp="y55eyxa3wiis">3</p>
              <p className="mt-0.5 text-[10.5px] leading-snug text-app-muted" data-id="element-4021" data-mp="135jchle30a2k">Follow-ups due</p>
            </div>
          </div>
        </aside>
      </div>

      <section className="overflow-hidden rounded-2xl bg-app-surface shadow-[0_1px_3px_rgba(20,20,24,0.07)]" data-id="element-4022" data-mp="2ipiwpyatla0t">
        <div className="flex items-center justify-between border-b border-app-divider px-4 py-2.5" data-id="element-4023" data-mp="2xe6nlr9q0vkf">
          <p className="text-[12px] font-semibold text-app" data-id="element-4024" data-mp="2smwycrs33u92">Live activity</p>
          <span className="text-[10.5px] text-app-subtle" data-id="element-4025" data-mp="36ebjb45jy5ir">Giga + team</span>
        </div>
        <div className="grid grid-cols-3" data-id="element-4026" data-mp="32d047skb12ot">
          {proof.map((entry, index) => <div key={`${entry.time}-${index}`} className={`flex min-w-0 gap-2.5 px-4 py-3 ${index > 0 ? 'border-l border-app-divider' : ''}`} data-id="element-4027" data-mp="32cqdg2rfqri2" data-mpi={index}>
              <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-app-subtle text-[var(--app-success)]" data-id="element-4028" data-mp="3bd6dt3cvtiip">
                <CheckIcon size={11} strokeWidth={2.8} data-id="element-4029" data-mp="1lz9uo6af1m7s" />
              </span>
              <div className="min-w-0" data-id="element-4030" data-mp="1h0ouu7z2t4jv">
                <p className="line-clamp-2 text-[11.5px] font-medium leading-[1.4] text-app" data-id="element-4031" data-mp="1h1rognns9l3h">{entry.text}</p>
                <p className="mt-1 text-[10px] text-app-subtle" data-id="element-4032" data-mp="175egj2uyps9h">{entry.time}</p>
              </div>
            </div>)}
        </div>
      </section>

      <p className="truncate text-[10.5px] text-app-subtle" data-id="element-4033" data-mp="xamgzthtkvc2">{trackName}</p>
    </div>;
}
export function TrackLinkedView({
  trackName
}: TrackDetailViewProps) {
  const inventory = linkedFor(trackName);
  return <div className="mx-auto grid w-full max-w-[1040px] grid-cols-1 gap-6 lg:grid-cols-2" data-id="element-4034" data-mp="1pwxfmylv8m68">
      <Panel data-id="element-4035" data-mp="1l06zycdz3slc">{inventory.left.map(category => <LinkedCategoryRows key={category.id} category={category} data-id="element-4036" data-mp="1l18pflvqkhml" />)}</Panel>
      <Panel data-id="element-4037" data-mp="1g74dspbizlf6">{inventory.right.map(category => <LinkedCategoryRows key={category.id} category={category} data-id="element-4038" data-mp="1g62vq0w3dyny" />)}</Panel>
    </div>;
}
export function TrackActivityView({
  trackName
}: TrackDetailViewProps) {
  const days = activityFor(trackName);
  const [query, setQuery] = useState('');
  const [type, setType] = useState<'All types' | ActivityTag>('All types');
  const [authors, setAuthors] = useState<Set<ActivityEntry['author']>>(() => new Set(['human', 'bot']));
  const q = query.trim().toLowerCase();
  const toggleAuthor = (author: ActivityEntry['author']) => setAuthors(current => {
    const next = new Set(current);
    if (next.has(author) && next.size > 1) next.delete(author);else next.add(author);
    return next;
  });
  const matches = (entry: ActivityEntry) => authors.has(entry.author) && (type === 'All types' || entry.tags.includes(type)) && (!q || entry.text.toLowerCase().includes(q));
  return <div className="mx-auto w-full max-w-[1040px]" data-id="element-4039" data-mp="ebhxtfibs6m3">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3" data-id="element-4040" data-mp="j3j7cto3zj8f">
        <div className="w-full sm:w-[260px]" data-id="element-4041" data-mp="n5msyb4hs47f">
          <SearchField value={query} onChange={setQuery} placeholder="Search this track" label="Search activity" data-id="element-4042" data-mp="n4cuvlxn2w0s" />
        </div>
        <div className="flex items-center gap-2" data-id="element-4043" data-mp="s2mqfxn9zymx">
          <SelectPill value={type} onChange={value => setType(value as typeof type)} options={['All types', 'Discussion', 'Decision', 'Issue', 'Progress', 'Customer signal', 'Metric']} label="Filter by event type" data-id="element-4044" data-mp="x3117ng9hmon" />
          <div className="inline-flex h-9 items-center gap-0.5 rounded-lg bg-app-subtle p-1" data-id="element-4045" data-mp="2o96w6dw8jjxb">
            {([['human', 'Human', UserIcon], ['bot', 'Bot', BotIcon]] as const).map(([key, label, Icon]) => <button key={key} type="button" onClick={() => toggleAuthor(key)} aria-pressed={authors.has(key)} className={`flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[12.5px] font-medium transition-colors duration-200 ease-out ${authors.has(key) ? 'bg-app-surface text-app shadow-[0_1px_2px_rgba(20,20,24,0.08)]' : 'text-app-subtle hover:text-app'}`} data-id="element-4046" data-mp="2y50gwkk3yjq6">
                <Icon size={12} data-id="element-4047" data-mp="3gwt05n9dkqkp" />
                {label}
              </button>)}
          </div>
        </div>
      </div>

      {days.map(day => {
      const entries = day.entries.filter(matches);
      if (!entries.length) return null;
      return <div key={day.date} className="mb-7 last:mb-0" data-id="element-4048" data-mp="1mjtno30akchn">
            <h3 className="mb-2.5 text-[13px] font-semibold text-app-muted" data-id="element-4049" data-mp="20rd7bs8favh">{day.date}</h3>
            <Panel data-id="element-4050" data-mp="6zkmst4eehqd">
              {entries.map((entry, index) => <Row key={`${day.date}-${entry.time}-${index}`} data-id="element-4051" data-mp="6z16fguec1eg" data-mpi={index}>
                  <Glyph icon={entry.author === 'bot' ? BotIcon : UserIcon} data-id="element-4052" data-mp="3oglp4qk271km" />
                  <span className="shrink-0 font-mono text-[12px] text-app-subtle" data-id="element-4053" data-mp="3tf5el0xv2vhq">{entry.time}</span>
                  <RowText title={entry.text} meta={entry.author === 'bot' ? 'Giga' : 'Team member'} data-id="element-4054" data-mp="3ffnlwahg2y33" />
                  <span className="hidden shrink-0 items-center gap-2.5 sm:flex" data-id="element-4055" data-mp="3ke7ayjrc4pzo">
                    {entry.tags.map(tag => <TagLegend key={tag} tag={tag} data-id="element-4056" data-mp="35hpz7rf8h9cl" />)}
                  </span>
                </Row>)}
            </Panel>
          </div>;
    })}
      {days.every(day => !day.entries.some(matches)) && <EmptyState data-id="element-4057" data-mp="13q33jckecq9s">Nothing here matches those filters.</EmptyState>}
    </div>;
}
function LinkedCategoryRows({
  category
}: {
  category: LinkedCategory;
}) {
  const Icon = CATEGORY_ICON[category.id] ?? Link2Icon;
  return <>
      <div className="flex items-center justify-between border-b border-app-divider px-4 py-2.5" data-id="element-4058" data-mp="34scfgezvsqpg">
        <p className="flex items-center gap-2 text-[12.5px] font-semibold text-app-muted" data-id="element-4059" data-mp="30q7o5mwwo65z">
          <Icon size={13} strokeWidth={1.9} className="text-app-subtle" data-id="element-4060" data-mp="2vquy6taqmiz6" />
          {category.name} · {category.items.length}
        </p>
        <button type="button" aria-label={`Add ${category.name}`} className="flex size-6 items-center justify-center rounded-md text-app-subtle transition-colors duration-200 ease-out hover:bg-app-hover hover:text-app" data-id="element-4061" data-mp="2qrrt58urccr7">
          <PlusIcon size={13} data-id="element-4062" data-mp="2h0dsgxr2l6wh" />
        </button>
      </div>
      {category.items.length ? category.items.map((item, index) => <Row key={`${item.title}-${index}`} data-id="element-4063" data-mp="28221uvq7e941" data-mpi={index}>
            <RowText title={item.title} meta={item.meta} data-id="element-4064" data-mp="2833gfa6fhdqi" />
            {item.badges?.map(badge => <span key={badge} className="shrink-0 rounded-md bg-app-subtle px-2 py-0.5 text-[11px] font-medium text-app-muted" data-id="element-4065" data-mp="231nsnacv8t4f">{badge}</span>)}
          </Row>) : <p className="border-b border-app-divider px-4 py-3.5 text-[13px] text-app-subtle last:border-0" data-id="element-4066" data-mp="1y5yrx3p4166j">No {category.name.toLowerCase()} linked yet.</p>}
    </>;
}
function TagLegend({
  tag
}: {
  tag?: ActivityTag;
}) {
  if (!tag) return null;
  return <span className="inline-flex shrink-0 items-center gap-1.5 text-[12px] font-medium text-app-muted" data-id="element-4067" data-mp="1xyws48tn6bui">
      <span className="size-1.5 rounded-full" style={{
      backgroundColor: TAG_DOT[tag]
    }} data-id="element-4068" data-mp="22vn5goe2atc8" />
      {tag}
    </span>;
}